import { ReduxRouter } from '@lagunovsky/redux-react-router'
import { doc, onSnapshot } from 'firebase/firestore'
import { useCallback, useContext, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { Route, Routes } from 'react-router-dom'
import { persistStore } from 'redux-persist'
import { PersistGate } from 'redux-persist/integration/react'

import { Account, AuthRoute } from './account/components'
import ProtectedRoute from './account/components/ProtectedRoute'
import registerAuthInterceptor from './account/services/registerAuthInterceptor'
import { setUser as setAppUser } from './app-reducer'
import { DefaultRoute } from './common/components'
import db from './configuration'
import { EditionChangeProvider } from './core/context/EditionChangeContext'
import { GoogleReCaptchaCustomProvider } from './core/context/GoogleReCaptchaContext'
import {
  Maintenance,
  MaintenanceProvider,
} from './core/context/MaintenanceContext'
import history from './core/services/history'
import { getUserProfile as userProfile } from './core/services/menu'
import {
  AddonPage,
  AdminSettingsPage,
  AdminsPage,
  ArtistsPage,
  AssetsPage,
  CampaignsPage,
  ClientsDetailsPage,
  CustomBillingFieldsPage,
  ClientsPage,
  CustomCodePage,
  DashboardPage,
  EmailTemplatesPage,
  FestivalSettingsPage,
  FestivalsPage,
  FinancialPage,
  GenerateTicketListPage,
  InfoPages,
  InvitesDownloadsPage,
  LinksPage,
  MapLocationsPage,
  MapTypesPage,
  MediaPhotoPage,
  MediaVideoPage,
  MemoriesPage,
  MenuPage,
  MyProfilePage,
  NewsPage,
  OrdersPage,
  PartnerCategoryPage,
  PartnerListPage,
  SEOPage,
  StagePage,
  StagesPage,
  SupportPage,
  UserGroupsPage,
  VouchersPage,
  VendorsPage,
  ScanTicketsPage,
  StaffPage,
} from './home/components'
import { GroupsPage } from './home/components/groups/GroupsPage'
import { VendorDetailsPage } from './home/components/vendors/VendorDetailsPage'
import { LocationDetailsPage } from './home/components/vendors/LocationDetailsPage'
import { NotificationPage } from './home/components/notifications/Page'
import { OrderDetailsPage } from './home/components/orders/OrderDetailsPage'
import { ReferralsPage } from './home/components/referral/ReferralsPage'
import { DownloadsPage } from './home/components/rewards/DownloadsPage'
import { InvitesPage } from './home/components/rewards/InvitesPage'
import { ActivityLogPage } from './home/components/settings/ActivityLogPage'
import { CustomThemePage } from './home/components/settings/CustomThemePage'
import { DefaultImagesPage } from './home/components/settings/DefaultImagesPage'
import { SettingsGeneralPage } from './home/components/settings/GeneralPage'
import { LanguagePage } from './home/components/settings/LanguagePage'
import { CreateUserGroupPage } from './home/components/settings/permissions/CreateUserGroupPage'
import { SettingPermissionsUsersPage } from './home/components/settings/permissions/UsersPage'
import { SettingPermissionsViewUserPage } from './home/components/settings/permissions/ViewUserPage'
import { ThemeImagesPage } from './home/components/settings/ThemeImagesPage'
import { ShippingBandsPage } from './home/components/shipping-bands/ShippingBandsPage'
import { TicketPage } from './home/components/tickets/Page'
import { TicketCategoriesPage } from './home/components/tickets/TicketCategoriesPage'
import { TopUpWristbandPage } from './home/components/top-up-wristband/TopUpWristbandPage'
import './index.css'
import store, { useAppDispatch, useAppSelector } from './store'
import { RedeemVouchersPage } from './home/components/rewards/RedeemVouchersPage'
registerAuthInterceptor()

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

const persistor = persistStore(store)

const AppRouter: React.FC = () => {
  const { maintenance, setMaintenance } = useContext(Maintenance)

  useEffect(() => {
    const docRef = doc(db, 'deploy_state', 'Y5QfzePx8MwaiHZytvof')
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const { isDeployInProgress } = docSnapshot.data()
        const maintenance = isDeployInProgress
        setMaintenance(maintenance)
      }
    })
    return () => {
      unsubscribe()
    }
  }, [setMaintenance])

  return (
    <Routes>
      <Route path="/" element={<AuthRoute />}>
        <>
          <Route index element={<DashboardPage />} />
          <Route path="/:festivalName/:editionId" element={<DashboardPage />} />
          <Route
            path="dashboard/:festivalName/:editionId"
            element={<DashboardPage />}
          />
          <Route path="my-profile" element={<MyProfilePage />} />
          <Route
            path="support/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SUPPORT">
                <SupportPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="home/:festivalName/:editionId"
            element={
              <ProtectedRoute page="STAGES">
                <StagesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="artists/:festivalName/:editionId"
            element={
              <ProtectedRoute page="ARTISTS">
                <ArtistsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="campaigns/:festivalName/:editionId"
            element={
              <ProtectedRoute page="CAMPAIGNS">
                <CampaignsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="stages/:festivalName/:editionId"
            element={
              <ProtectedRoute page="STAGES">
                <StagesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="stages/:festivalName/:editionId/:stageId"
            element={
              <ProtectedRoute page="STAGES">
                <StagePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="map/types/:festivalName/:editionId"
            element={
              <ProtectedRoute page="LOCATIONS_TYPE">
                <MapTypesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="map/locations/:festivalName/:editionId"
            element={
              <ProtectedRoute page="LOCATIONS_MAP">
                <MapLocationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="scan-tickets/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SCAN_TICKETS">
                <ScanTicketsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="notifications/:festivalName/:editionId"
            element={
              <ProtectedRoute page="NOTIFICATIONS">
                <NotificationPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="tickets/tickets/:festivalName/:editionId/:categoryId"
            element={
              <ProtectedRoute page="TICKETS">
                <TicketPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="orders/:festivalName/:editionId"
            element={
              <ProtectedRoute page="ORDERS">
                <OrdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="orders/:festivalName/:editionId/:orderId"
            element={
              <ProtectedRoute page="ORDERS">
                <OrderDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="tickets/add-ons/:festivalName/:editionId"
            element={
              <ProtectedRoute page="TICKETS_ADD_ONS">
                <AddonPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="clients/:festivalName/:editionId"
            element={
              <ProtectedRoute page="CLIENTS">
                <ClientsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="clients/:festivalName/:editionId/:clientId"
            element={
              <ProtectedRoute page="CLIENTS">
                <ClientsDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="info-pages/:festivalName/:editionId"
            element={
              <ProtectedRoute page="INFO">
                <InfoPages />
              </ProtectedRoute>
            }
          />

          <Route
            path="news/:festivalName/:editionId"
            element={
              <ProtectedRoute page="NEWS">
                <NewsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="partners/categories/:festivalName/:editionId"
            element={
              <ProtectedRoute page="PARTNERS_CATEGORIES">
                <PartnerCategoryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="media/photo/:festivalName/:editionId"
            element={
              <ProtectedRoute page="PHOTO_GALLERY">
                <MediaPhotoPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="assets/:festivalName/:editionId"
            element={
              <ProtectedRoute page="UPLOAD_IMAGES">
                <AssetsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="media/video/:festivalName/:editionId"
            element={
              <ProtectedRoute page="VIDEO_GALLERY">
                <MediaVideoPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="partners/partners/:festivalName/:editionId"
            element={
              <ProtectedRoute page="PARTNERS_LIST">
                <PartnerListPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="memories/:festivalName/:editionId"
            element={
              <ProtectedRoute page="MEMORIES">
                <MemoriesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/seo/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <SEOPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/financial/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <FinancialPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/menu/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <MenuPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/general/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <SettingsGeneralPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/default-images/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <DefaultImagesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/theme/images/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <ThemeImagesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/theme/general/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <CustomThemePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/notifications/links/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <LinksPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/notifications/custom-fields/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <CustomBillingFieldsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/custom-code/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <CustomCodePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/email-templates/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <EmailTemplatesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/languages/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <LanguagePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/activity-log/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SETTINGS">
                <ActivityLogPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/permissions/user-groups/:festivalName/:editionId"
            element={
              // TODO: check
              <ProtectedRoute page="SETTINGS">
                <UserGroupsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/permissions/user-groups/:festivalName/:editionId/:groupId"
            element={
              <ProtectedRoute page="SETTINGS">
                <CreateUserGroupPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="rewards/invites/:festivalName/:editionId"
            element={
              <ProtectedRoute page="INVITES">
                <InvitesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="rewards/vouchers/:festivalName/:editionId"
            element={
              <ProtectedRoute page="VOUCHERS">
                <VouchersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="rewards/redeem-vouchers/:festivalName/:editionId"
            element={
              <ProtectedRoute page="VOUCHERS">
                <RedeemVouchersPage />
              </ProtectedRoute>
            }
          />

          {/* <Route
            path="rewards/runa/:festivalName/:editionId"
            element={<RunaPage />}
          /> */}

          <Route
            path="generate-tickets/:festivalName/:editionId"
            element={
              <ProtectedRoute page="GENERATE_TICKETS">
                <GenerateTicketListPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="shipping-bands/:festivalName/:editionId"
            element={
              <ProtectedRoute page="SHIPPING_BANDS">
                <ShippingBandsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="top-up-wristband/:festivalName/:editionId"
            element={
              <ProtectedRoute page="TOP_UP_WRISTBAND">
                <TopUpWristbandPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="tickets/categories/:festivalName/:editionId"
            element={
              <ProtectedRoute page="TICKETS_CATEGORY">
                <TicketCategoriesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/permissions/users/:festivalName/:editionId"
            element={
              <ProtectedRoute page="PERMISSIONS_EDIT_USER">
                <SettingPermissionsUsersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="settings/permissions/users/:festivalName/:editionId/view/:userId"
            element={
              <ProtectedRoute page="PERMISSIONS_EDIT_USER">
                <SettingPermissionsViewUserPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="rewards/vouchers/downloads/:festivalName/:editionId"
            element={
              <ProtectedRoute page="VOUCHERS">
                <DownloadsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="rewards/invites/downloads/:festivalName/:editionId"
            element={
              <ProtectedRoute page="INVITES">
                <InvitesDownloadsPage />
              </ProtectedRoute>
            }
          />

          {/* TODO: request permission */}
          <Route path="groups" element={<GroupsPage />} />

          <Route
            path="referrals/:festivalName/:editionId"
            element={
              <ProtectedRoute page="REFERRALS">
                <ReferralsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="vendors/:festivalName/:editionId"
            element={
              <ProtectedRoute page="VENDORS">
                <VendorsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="staff/:festivalName/:editionId"
            element={
              <ProtectedRoute page="STAFF">
                <StaffPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="vendors/:festivalName/:editionId/:vendorId"
            element={
              <ProtectedRoute page="VENDORS">
                <VendorDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="vendors/:festivalName/:editionId/:vendorId/location/:locationId"
            element={
              <ProtectedRoute page="VENDORS">
                <LocationDetailsPage />
              </ProtectedRoute>
            }
          />
        </>

        <>
          <Route path="settings/festivals" element={<FestivalsPage />} />
          <Route
            path="settings/festivals/:editionId"
            element={<FestivalSettingsPage />}
          />
          <Route path="settings/admins" element={<AdminsPage />} />
          <Route
            path="settings/admins/:adminId"
            element={<AdminSettingsPage />}
          />
        </>
      </Route>
      <Route path="*" element={<DefaultRoute />} />
    </Routes>
  )
}

const MainRouter = () => {
  const account = useAppSelector((state) => state.account)
  const dispatch = useAppDispatch()

  const getUserProfile = useCallback(async () => {
    if (!account?.login?.isLoggedIn) {
      return
    }
    try {
      const { data, status } = await userProfile()

      if (status === 200) {
        dispatch(setAppUser({ user: data }))
      }
    } catch (err) {
      console.log(err)
    }
  }, [account.login.isLoggedIn])

  useEffect(() => {
    getUserProfile()
  }, [getUserProfile])

  return (
    <Routes location={location}>
      <Route path="account/*" element={<Account />} />
      <Route path="*" element={<AppRouter />} />
    </Routes>
  )
}

root.render(
  <GoogleReCaptchaCustomProvider>
    <MaintenanceProvider>
      <EditionChangeProvider>
        <Provider store={store}>
          <PersistGate persistor={persistor}>
            <ReduxRouter history={history}>
              <MainRouter />
            </ReduxRouter>
          </PersistGate>
        </Provider>
      </EditionChangeProvider>
    </MaintenanceProvider>
  </GoogleReCaptchaCustomProvider>,
)
